// Keeps the page from getting shorter while a modal is open. On Brave for
// iOS, a page that stops being scrollable under a modal (Settings → Classic)
// snaps to its top, Brave re-applies its address-bar inset, and the viewport
// moves twice in a few ms; the modal, re-centred a frame late, jumped
// 90 → 30 → 120 → 90 (trace from the user's iPhone, 2026-10-27). Growing is
// fine, and the hold rises with it, so nothing shrinks back while held.
// A page field's keyboard closing behind the modal would pull a scrolled page
// down step by step as the screen grows; the hold leaves room for the screen
// it's about to have (closingKeyboard.ts).

import { closingKeyboardHeight } from './closingKeyboard';

let holds = 0;
let restore: (() => void) | null = null;

/** Holds the page's height; call the returned function to let go. */
export function holdPageHeight(): () => void {
  if (holds++ === 0) {
    const root = document.documentElement;
    const previous = root.style.minHeight;
    const hold = () => {
      const height = Math.max(
        root.scrollHeight,
        window.scrollY + (closingKeyboardHeight() ?? 0),
      );
      if (height > (parseFloat(root.style.minHeight) || 0))
        root.style.minHeight = `${height}px`;
    };
    hold();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(hold);
    // The content, not body: html and body are height: 100%, so body's box
    // never grows with the page (Settings → Table grew it 900 → 1092 and
    // the hold stayed at 900, 2026-10-28).
    for (const child of document.body.children) observer?.observe(child);
    restore = () => {
      observer?.disconnect();
      root.style.minHeight = previous;
    };
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--holds === 0) {
      restore?.();
      restore = null;
    }
  };
}
